import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { DateRangePicker } from "./DateRangePicker";
import { DateRange } from "react-day-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Globe, Smartphone, Monitor } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { useAnalyticsStats } from "@/hooks/useAnalytics";
import { useLeadStats } from "@/hooks/useLeads";
import { useFinanceStats } from "@/hooks/usePayments";
import { differenceInDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatBRL } from "@/lib/format";
import { leadSourceLabel } from "@/lib/leadSource";

const SOURCE_COLORS = [
  "bg-primary",
  "bg-ok",
  "bg-info",
  "bg-warn",
  "bg-notice",
  "bg-muted-foreground",
];
const fmtBRL = (v: number) => formatBRL(v);

interface AnalyticsProps {
  /** Rendered inside the Financeiro tab — hides the standalone h1. */
  embedded?: boolean;
  /** Receita vs despesa (12 meses) — shown under "Ver mais". */
  timeline?: { month: string; revenue: number; expense: number }[];
}

export function Analytics({ embedded = false, timeline }: AnalyticsProps) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
  });
  const [moreOpen, setMoreOpen] = useState(false);

  const days =
    dateRange?.from && dateRange?.to ? differenceInDays(dateRange.to, dateRange.from) + 1 : 30;

  const { data: analyticsData, isLoading: analyticsLoading } = useAnalyticsStats(days);
  const { data: leadStats, isLoading: leadsLoading } = useLeadStats();
  const { data: finance } = useFinanceStats();

  const isLoading = analyticsLoading || leadsLoading;

  const pageViewsByDay = analyticsData?.pageViewsByDay ?? [];
  const maxPv = Math.max(1, ...pageViewsByDay.map((d) => d.count));

  const topPages = useMemo(() => {
    const pages = (analyticsData?.topPages ?? [])
      .filter((p) => !p.path.startsWith("/admin"))
      .slice(0, 5);
    const max = Math.max(1, ...pages.map((p) => p.views));
    return pages.map((p) => ({ ...p, pct: Math.round((p.views / max) * 100) }));
  }, [analyticsData?.topPages]);

  const sources = useMemo(() => {
    const entries = Object.entries(leadStats?.bySource ?? {}) as [string, number][];
    const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
    return entries
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({
        label: leadSourceLabel(name),
        pct: `${Math.round((value / total) * 100)}%`,
        color: SOURCE_COLORS[i % SOURCE_COLORS.length],
      }));
  }, [leadStats?.bySource]);

  const leadsLast30 = leadStats?.last7Days ?? 0;
  const totalLeads = leadStats?.total ?? 0;
  const monthExpenses = finance?.expenseMonthly ?? 0;
  const costPerLead =
    leadsLast30 > 0 && monthExpenses > 0 ? fmtBRL(monthExpenses / leadsLast30) : "—";

  const devices = useMemo(() => {
    const { desktop = 0, mobile = 0, tablet = 0 } = analyticsData?.deviceBreakdown ?? {};
    const total = desktop + mobile + tablet || 1;
    return [
      {
        device: "Desktop",
        icon: Monitor,
        visits: desktop,
        percentage: Number(((desktop / total) * 100).toFixed(1)),
      },
      {
        device: "Mobile",
        icon: Smartphone,
        visits: mobile,
        percentage: Number(((mobile / total) * 100).toFixed(1)),
      },
      {
        device: "Tablet",
        icon: Globe,
        visits: tablet,
        percentage: Number(((tablet / total) * 100).toFixed(1)),
      },
    ];
  }, [analyticsData?.deviceBreakdown]);

  const leadStatusData = useMemo(() => {
    if (!leadStats) return [];
    return [
      { status: "Novos", count: leadStats.new },
      { status: "Em contato", count: leadStats.contacted },
      { status: "Qualificados", count: leadStats.qualified },
      { status: "Convertidos", count: leadStats.converted },
      { status: "Perdidos", count: leadStats.lost },
    ];
  }, [leadStats]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-[14px] border border-border bg-card px-5 py-[18px]">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-7 w-20" />
            <Skeleton className="mt-2 h-3 w-16" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {!embedded ? (
          <PageHeader title="Analytics" description="Acompanhe o desempenho do seu site" />
        ) : (
          <span />
        )}
        <DateRangePicker date={dateRange} onDateChange={setDateRange} />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-4">
        <Metric
          label="Visitas"
          value={(analyticsData?.pageViews ?? 0).toLocaleString("pt-BR")}
          foot={`últimos ${days} dias`}
          tone="text-ok"
        />
        <Metric
          label="Envios de formulário"
          value={(analyticsData?.formSubmits ?? 0).toLocaleString("pt-BR")}
          foot="pelo site"
          tone="text-muted-foreground"
        />
        <Metric
          label="Leads gerados"
          value={totalLeads.toLocaleString("pt-BR")}
          foot={`${leadsLast30} nos últimos 7 dias`}
          tone="text-primary"
        />
        <Metric
          label="Custo por lead"
          value={costPerLead}
          foot={monthExpenses > 0 ? "despesas do mês ÷ leads (7 dias)" : "sem despesas no mês"}
          tone="text-muted-foreground"
        />
      </div>

      <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-[15px] font-bold">Visitas por dia</h2>
          <span className="text-xs text-muted-foreground">últimos 14 dias</span>
        </div>
        <div className="flex h-[140px] items-end gap-2">
          {pageViewsByDay.map((d) => (
            <div
              key={d.date}
              title={`${format(new Date(d.date + "T12:00:00"), "dd/MM", { locale: ptBR })} · ${d.count}`}
              className="min-h-[3px] flex-1 rounded-b-[3px] rounded-t-[6px] bg-accent transition-all"
              style={{ height: `${Math.max(3, (d.count / maxPv) * 100)}%` }}
            />
          ))}
        </div>
      </section>

      <div className="grid items-start gap-4 lg:grid-cols-[1.3fr_1fr]">
        <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
          <h2 className="mb-4 text-[15px] font-bold">Páginas mais vistas</h2>
          {topPages.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">
              Nenhuma página visitada no período.
            </p>
          )}
          {topPages.map((p) => (
            <div key={p.path} className="border-b border-border py-[11px] last:border-b-0">
              <div className="flex items-center justify-between text-[12.5px]">
                <span className="truncate font-mono text-muted-foreground">{p.path}</span>
                <span className="ml-3 font-bold">{p.views.toLocaleString("pt-BR")}</span>
              </div>
              <div className="mt-[7px] h-[5px] overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${p.pct}%` }} />
              </div>
            </div>
          ))}
        </section>
        <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
          <h2 className="mb-4 text-[15px] font-bold">Origem dos leads</h2>
          {sources.length === 0 && (
            <EmptyState size="sm" title="Nenhum lead registrado." className="py-2" />
          )}
          {sources.map((s) => (
            <div key={s.label} className="flex items-center gap-3 py-2.5">
              <span className={`h-[9px] w-[9px] rounded-full ${s.color}`} />
              <span className="flex-1 text-[13px]">{s.label}</span>
              <span className="text-[13px] font-bold">{s.pct}</span>
            </div>
          ))}
        </section>
      </div>

      <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${moreOpen ? "rotate-180" : ""}`}
            />
            {moreOpen ? "Ver menos" : "Ver mais"}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-4 pt-4">
          {timeline && timeline.length > 0 && (
            <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
              <h2 className="mb-4 text-[15px] font-bold">Receita vs despesa — últimos 12 meses</h2>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeline}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis
                      tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                      className="text-xs"
                    />
                    <Tooltip
                      formatter={(v: number) => fmtBRL(v)}
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      name="Receita"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="expense"
                      name="Despesa"
                      stroke="hsl(var(--bad))"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
              <h2 className="mb-4 text-[15px] font-bold">Status dos leads</h2>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={leadStatusData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" className="text-xs" />
                    <YAxis dataKey="status" type="category" width={100} className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
              <h2 className="mb-4 text-[15px] font-bold">Dispositivos</h2>
              <div className="space-y-3">
                {devices.map((d) => (
                  <div key={d.device} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <d.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-[13px]">{d.device}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-[13px] font-bold">
                        {d.visits.toLocaleString("pt-BR")}
                      </div>
                      <div className="text-[11.5px] text-muted-foreground">{d.percentage}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <section className="rounded-2xl border border-border bg-card px-6 py-[22px] text-center shadow-card">
              <h2 className="mb-3 text-[15px] font-bold">Eventos rastreados</h2>
              <div className="font-display text-3xl font-bold text-primary">
                {analyticsData?.totalEvents ?? 0}
              </div>
              <p className="mt-1 text-[12.5px] text-muted-foreground">no período selecionado</p>
            </section>
            <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
              <h2 className="mb-3 text-[15px] font-bold">Performance de leads</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-[13px]">
                  <span>Novos (7 dias)</span>
                  <Badge variant="outline" className="border-0 bg-accent text-primary">
                    {leadStats?.last7Days ?? 0}
                  </Badge>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span>Total</span>
                  <Badge variant="outline">{leadStats?.total ?? 0}</Badge>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span>Conversão</span>
                  <Badge variant="outline" className="border-0 bg-ok-soft text-ok">
                    {leadStats?.conversionRate ?? 0}%
                  </Badge>
                </div>
              </div>
            </section>
            <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
              <h2 className="mb-3 text-[15px] font-bold">Tipos de evento</h2>
              <div className="space-y-3">
                {analyticsData?.byEventType &&
                  Object.entries(analyticsData.byEventType).map(([type, count]) => (
                    <div key={type} className="flex justify-between text-[13px]">
                      <span className="font-mono text-muted-foreground">{type}</span>
                      <Badge variant="secondary">{count as number}</Badge>
                    </div>
                  ))}
              </div>
            </section>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function Metric({
  label,
  value,
  foot,
  tone,
}: {
  label: string;
  value: string;
  foot: string;
  tone: string;
}) {
  return (
    <div className="rounded-[14px] border border-border bg-card px-5 py-[18px] shadow-card">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-[9px] font-display text-2xl font-bold tracking-[-0.035em]">{value}</div>
      <div className={`mt-[5px] text-[11.5px] font-semibold ${tone}`}>{foot}</div>
    </div>
  );
}
